/**
 * Exclusive Accordion Behavior with Smooth Transitions
 * Ensures only one <details> element is open at a time within each accordion group.
 * Animates closing via CSS grid-template-rows transition.
 *
 * Usage:
 *   Add data-accordion-group="groupName" to container
 *   Add data-accordion-exclusive="true" to enable (default true if attribute present)
 *   Add data-accordion-exclusive="false" to allow multiple open
 *
 * Optional persistence (opt-in):
 *   Add data-accordion-persist="true" to container
 *   Add data-accordion-namespace="uniqueKey" (e.g. dr-product-panel-{productId})
 *   Add data-accordion-item-id="itemId" on each <details> for stable mapping
 *   State is saved to localStorage and restored on init/section:load
 *
 * Supports:
 *   - Smooth animated open/close transitions (including self-close)
 *   - Multiple independent groups on same page
 *   - Dynamically loaded content
 *   - Shopify section:load events
 *   - Reload persistence (when data-accordion-persist is set)
 */
(() => {
  // Guard against double initialization
  if (window.DR_ACCORDION_LOADED) return;
  window.DR_ACCORDION_LOADED = true;

  // Animation duration (should match CSS transition)
  const ANIMATION_DURATION = 280;
  const STORAGE_PREFIX = 'dr-accordion-state:v1:';

  /**
   * Find the accordion group container for a given element
   * @param {Element} el - The element to search from
   * @returns {Element|null} - The container with data-accordion-group
   */
  function findContainer(el) {
    return el?.closest?.('[data-accordion-group]');
  }

  /**
   * Check if a container has exclusive behavior enabled
   * @param {Element} container - The accordion group container
   * @returns {boolean} - True if exclusive (default), false if explicitly disabled
   */
  function isExclusive(container) {
    const value = container.getAttribute('data-accordion-exclusive');
    return value !== 'false';
  }

  /**
   * Check if accordion state persistence is enabled for a container
   * @param {Element} container - The accordion group container
   * @returns {boolean} - True if persistence is enabled
   */
  function shouldPersist(container) {
    if (!container) return false;
    const value = container.getAttribute('data-accordion-persist');
    return value !== null && value !== 'false';
  }

  /**
   * Get localStorage safely (can fail in private/restricted contexts)
   * @returns {Storage|null}
   */
  function getStorage() {
    try {
      return window.localStorage;
    } catch (_err) {
      return null;
    }
  }

  /**
   * Build a storage key for a specific accordion container
   * @param {Element} container - The accordion group container
   * @returns {string|null}
   */
  function getStorageKey(container) {
    const group = container?.getAttribute('data-accordion-group');
    if (!group) return null;

    const namespace = container.getAttribute('data-accordion-namespace') || window.location.pathname;
    return `${STORAGE_PREFIX}${namespace}:${group}`;
  }

  /**
   * Collect details elements that belong directly to a container's accordion group
   * @param {Element} container - The accordion group container
   * @returns {HTMLDetailsElement[]}
   */
  function getGroupDetails(container) {
    const items = container.querySelectorAll(':scope > details, :scope > * > details');
    return [...items].filter((item) => (
      item instanceof HTMLDetailsElement
      && item.closest('[data-accordion-group]') === container
    ));
  }

  /**
   * Resolve the persistent identifier for a details item
   * @param {HTMLDetailsElement} details - The details element
   * @param {number} index - Fallback index
   * @returns {string}
   */
  function getItemId(details, index) {
    return details.getAttribute('data-accordion-item-id') || String(index);
  }

  /**
   * Save current open state of a container
   * @param {Element} container - The accordion group container
   */
  function saveState(container) {
    if (!shouldPersist(container)) return;

    const storage = getStorage();
    const key = getStorageKey(container);
    if (!storage || !key) return;

    const openItems = getGroupDetails(container)
      .map((details, index) => ({ details, id: getItemId(details, index) }))
      .filter((item) => item.details.open)
      .map((item) => item.id);

    try {
      storage.setItem(key, JSON.stringify(openItems));
    } catch (_err) {
      // Ignore storage quota and access errors.
    }
  }

  /**
   * Restore previously saved open state for a container
   * @param {Element} container - The accordion group container
   */
  function restoreState(container) {
    if (!shouldPersist(container)) return;

    const storage = getStorage();
    const key = getStorageKey(container);
    if (!storage || !key) return;

    let parsed = null;
    try {
      parsed = JSON.parse(storage.getItem(key));
    } catch (_err) {
      parsed = null;
    }

    if (!Array.isArray(parsed)) return;

    const openSet = new Set(parsed.map(String));
    const detailsList = getGroupDetails(container);

    detailsList.forEach((details, index) => {
      const shouldOpen = openSet.has(getItemId(details, index));
      if (shouldOpen) {
        details.setAttribute('open', '');
      } else {
        details.removeAttribute('open');
      }
    });

    if (isExclusive(container)) {
      let foundOpen = false;
      detailsList.forEach((details) => {
        if (!details.open) return;
        if (foundOpen) {
          details.removeAttribute('open');
        } else {
          foundOpen = true;
        }
      });
    }
  }

  /**
   * Restore persisted state for groups in a root node
   * @param {ParentNode} root - Root node to scan
   */
  function restorePersistedState(root = document) {
    if (!(root instanceof Element || root instanceof Document || root instanceof DocumentFragment)) return;

    const groups = [];
    if (root instanceof Element && root.matches('[data-accordion-group][data-accordion-persist]')) {
      groups.push(root);
    }

    root.querySelectorAll?.('[data-accordion-group][data-accordion-persist]').forEach((group) => {
      groups.push(group);
    });

    groups.forEach((group) => restoreState(group));
  }

  /**
   * Animate closing a details element before removing open attribute
   * @param {HTMLDetailsElement} details - The details element to close
   * @param {Element|null} container - Optional pre-resolved container
   * @returns {boolean} - True if animation started, false if skipped
   */
  function animateClose(details, container = null) {
    // Skip if already closing or not open
    if (details.classList.contains('is-closing') || !details.open) return false;

    // Cancel any opening state before starting the close transition
    details.classList.remove('is-opening');

    // Add closing class to trigger CSS transition
    details.classList.add('is-closing');

    // After animation completes, actually close it
    setTimeout(() => {
      details.removeAttribute('open');
      details.classList.remove('is-closing');
      saveState(container || findContainer(details));
    }, ANIMATION_DURATION);

    return true;
  }

  /**
   * Animate opening a details element from a rendered collapsed state
   * @param {HTMLDetailsElement} details - The details element to open
   * @returns {boolean} - True if animation started, false if skipped
   */
  function animateOpen(details) {
    if (details.open || details.classList.contains('is-opening')) return false;

    // Native <details> hides closed content, so expose it while the opening
    // class holds the animated panel at its collapsed starting state.
    details.classList.add('is-opening');
    details.setAttribute('open', '');

    // Flush the collapsed open state before transitioning to the default state.
    void details.offsetHeight;
    requestAnimationFrame(() => {
      details.classList.remove('is-opening');
    });

    return true;
  }

  /**
   * Handle clicks on summaries so opening and closing can both be staged
   * @param {Event} e - The click event
   */
  function onSummaryClick(e) {
    const summary = e.target.closest('summary');
    if (!summary) return;

    const details = summary.parentElement;
    if (!(details instanceof HTMLDetailsElement)) return;

    // Only handle accordion groups (not random details elements)
    const container = findContainer(details);
    if (!container) return;

    // Prevent the browser from instantly toggling so both directions animate.
    e.preventDefault();

    if (details.open) {
      animateClose(details, container);
    } else {
      animateOpen(details);
    }
  }

  /**
   * Handle toggle events on details elements
   * Closes other open details in the same group when one opens
   * @param {Event} e - The toggle event
   */
  function onToggle(e) {
    const details = e.target;

    // Only handle actual details elements
    if (!(details instanceof HTMLDetailsElement)) return;

    // Find the accordion group container
    const container = findContainer(details);
    if (!container) return;

    if (details.open && isExclusive(container)) {
      // Animate close all other open details in this group
      container.querySelectorAll(':scope > details[open], :scope > * > details[open]').forEach((other) => {
        if (other !== details && other.closest('[data-accordion-group]') === container) {
          animateClose(other, container);
        }
      });
    }

    saveState(container);
  }

  /**
   * Initialize the toggle listener
   * Uses event delegation for efficiency and dynamic content support
   */
  function init(root = document) {
    // Remove any existing listeners to prevent duplicates
    document.removeEventListener('toggle', onToggle, true);
    document.removeEventListener('click', onSummaryClick, true);

    // Restore persisted accordion state before attaching listeners
    restorePersistedState(root);

    // Add listener in capture phase to catch all toggle events
    document.addEventListener('toggle', onToggle, true);
    // Add click listener to intercept closing clicks on summaries
    document.addEventListener('click', onSummaryClick, true);
  }

  // Initialize on DOM ready or immediately if already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  // Re-initialize on Shopify section load (theme editor)
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
